import { Link, useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Calendar, MapPin, Clock, Sparkles, Quote } from 'lucide-react';
import { findEventOrNull } from '@/lib/mock';
import Logo from '@/components/Logo';
import { useTheme } from '@/components/ThemeProvider';
import { API_BASE } from '@/lib/utils';
import { bumpEventFunnel } from '@/lib/funnel-beacon';
import { resolveMediaUrl } from '@/lib/media-api';
import { googleFontsHref, GOOGLE_FONTS } from '@/lib/google-fonts';
import { applyBrandDocumentEffects, clearTenantDocumentEffects } from '@/lib/tenant-brand';
import DemoEventLanding from '@/pages/public/DemoEventLanding';
import PublicEventBlocked, { getBlockedPresentation } from '@/components/PublicEventBlocked';
import SpeakersGrid from '@/components/SpeakersGrid';
import ThermeGroundbreakingLanding from '@/pages/public/grandfathered/ThermeGroundbreakingLanding';
import ThermeGroundbreakingLandingClassic from '@/pages/public/grandfathered/ThermeGroundbreakingLandingClassic';
import { formatEventDateRange } from '@/lib/event-dates';
import { fetchPublicEventBySlug } from '@/lib/public-event';

const GLOBAL_DEMO_SLUG = 'stride-2026';

function RegisterCta({ href, external, className, children, testId }) {
  if (external && href) {
    return (
      <a href={href} className={className} target="_blank" rel="noreferrer" data-testid={testId}>
        {children}
      </a>
    );
  }
  return (
    <Link to={href} className={className} data-testid={testId}>
      {children}
    </Link>
  );
}

// const API_BASE = typeof window !== "undefined" ? "" : "http://localhost:8001";

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS_FULL = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
// Public-landing logo: event logo → workspace brand logo → AivoryEMS mark.
function EventLogo({ event, brand }) {
  const src = resolveMediaUrl(event?.logo || brand?.logoUrl);
  const label = event?.title || brand?.productName || 'Event logo';
  if (src) {
    return (
      <img
        src={src}
        alt={label}
        className="h-8 w-auto max-w-[160px] object-contain"
        data-testid="event-logo"
      />
    );
  }
  return <Logo />;
}

function formatDateRange(startDate) {
  if (!startDate) return '';
  const d = new Date(startDate + 'T00:00:00');
  const day = DAYS[d.getDay()];
  const date = d.getDate();
  const month = MONTHS_FULL[d.getMonth()];
  return `${day} ${date} ${month} from 9:15AM Onwards`;
}

export default function EventLanding({ slugOverride }) {
  const { setTheme } = useTheme();
  const params = useParams();
  // slugOverride is set when the SPA is mounted under a white-label custom
  // domain (App.js detects window.location.hostname matches an event's
  // customDomain). useParams stays empty in that case because the route
  // hijacked at the root is `/`, not `/e/:slug`.
  const slug = slugOverride || params.slug;
  // Local copy of the events list. Seeds from window.__AIVORY_BOOTSTRAP__ if
  // App.js's bootstrap fetch already finished, otherwise stays null and we
  // fetch our own copy in the effect below. This is the fix for the server
  // race where App.js's 4s fallback timer fires before /api/bootstrap-data
  // finishes loading on cold-cache visits -- without this, useMemo runs
  // once with an empty global and renders "Event not found" forever.
  const [bootstrapEvents, setBootstrapEvents] = useState(() => {
    const cached = window.__AIVORY_BOOTSTRAP__?.events;
    return Array.isArray(cached) ? cached : null;
  });
  const [bootstrapLoading, setBootstrapLoading] = useState(() => {
    const cached = window.__AIVORY_BOOTSTRAP__?.events;
    return !Array.isArray(cached);
  });
  const [publicEvent, setPublicEvent] = useState(null);
  const [publicLoading, setPublicLoading] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [govRegistration, setGovRegistration] = useState(null);

  // Always re-fetch on mount, even if window.__AIVORY_BOOTSTRAP__.events is
  // already populated. App.js's bootstrap is loaded once at app start and
  // can be stale by the time a visitor lands here -- in particular,
  // event.theme updated in admin won't be reflected if we trust the cache.
  // We keep the cached value as the initial render so there's no blank frame.
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/bootstrap-data`, { cache: 'no-store' });
        if (!res.ok) throw new Error(`bootstrap ${res.status}`);
        const payload = await res.json();
        const events = Array.isArray(payload?.data?.events) ? payload.data.events : [];
        if (!cancel) {
          setBootstrapEvents(events);
          window.__AIVORY_BOOTSTRAP__ = window.__AIVORY_BOOTSTRAP__ || {};
          window.__AIVORY_BOOTSTRAP__.events = events;
        }
      } catch {
        if (!cancel && !Array.isArray(bootstrapEvents)) setBootstrapEvents([]);
      } finally {
        if (!cancel) setBootstrapLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bootstrap only includes the default tenant; tenant-scoped events need the public API.
  useEffect(() => {
    const needle = String(slug || '').trim().toLowerCase();
    if (!needle || needle === GLOBAL_DEMO_SLUG) return undefined;
    const inBootstrap = Array.isArray(bootstrapEvents)
      && bootstrapEvents.some(
        (row) => String(row?.slug || '').trim().toLowerCase() === needle
          || String(row?.id || '').trim().toLowerCase() === needle,
      );
    if (inBootstrap) {
      setPublicEvent(null);
      setPublicLoading(false);
      return undefined;
    }
    let cancel = false;
    setPublicLoading(true);
    (async () => {
      try {
        const event = await fetchPublicEventBySlug(needle);
        if (!cancel) setPublicEvent(event);
      } catch {
        if (!cancel) setPublicEvent(null);
      } finally {
        if (!cancel) setPublicLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [slug, bootstrapEvents]);

  useEffect(() => {
    const needle = String(slug || '').trim().toLowerCase();
    if (!needle) return undefined;
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/public/events/${encodeURIComponent(needle)}/recommendations`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancel) setRecommendations(Array.isArray(data.recommendations) ? data.recommendations.slice(0, 4) : []);
      } catch {
        if (!cancel) setRecommendations([]);
      }
    })();
    return () => { cancel = true; };
  }, [slug]);

  useEffect(() => {
    const needle = String(slug || '').trim().toLowerCase();
    if (!needle) return undefined;
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/public/register-meta?slug=${encodeURIComponent(needle)}`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancel) setGovRegistration(data?.registration || null);
      } catch {
        if (!cancel) setGovRegistration(null);
      }
    })();
    return () => { cancel = true; };
  }, [slug]);

  // Bootstrap wins over mock.
  // is a fixture that exists for the dev-experience demo and never has a
  // theme field; if a real event happens to share a slug with a mock entry
  // and we returned the mock first, admin updates (theme, status, etc.)
  // would silently lose. Mock is now only used when bootstrap has no match.
  const e = useMemo(() => {
    const needle = String(slug || '').trim();
    if (Array.isArray(bootstrapEvents)) {
      const fromBootstrap = bootstrapEvents.find(
        (row) => String(row?.slug || '').trim() === needle || String(row?.id || '').trim() === needle,
      );
      if (fromBootstrap) return fromBootstrap;
    }
    if (publicEvent) return publicEvent;
    return findEventOrNull(needle);
  }, [slug, bootstrapEvents, publicEvent]);
  const [published, setPublished] = useState(null);
  const [sessionsData, setSessionsData] = useState([]);
  const [speakersData, setSpeakersData] = useState([]);
  const [sponsorsData, setSponsorsData] = useState([]);
  const [ticketsData, setTicketsData] = useState([]);
  const [workspaceBrand, setWorkspaceBrand] = useState(null);
  const [demoBundle, setDemoBundle] = useState(null);
  const [demoLoading, setDemoLoading] = useState(() => String(slug || '').trim().toLowerCase() === GLOBAL_DEMO_SLUG);

  const isGlobalDemo = useMemo(() => {
    const needle = String(slug || '').trim().toLowerCase();
    if (needle === GLOBAL_DEMO_SLUG) return true;
    return Boolean(e?.globalDemo);
  }, [slug, e]);

  useEffect(() => {
    if (!isGlobalDemo) {
      setDemoLoading(false);
      return undefined;
    }
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/public/events/${GLOBAL_DEMO_SLUG}`, { cache: 'no-store' });
        if (!res.ok) throw new Error(`demo ${res.status}`);
        const body = await res.json();
        if (!cancel) setDemoBundle(body);
      } catch {
        if (!cancel) setDemoBundle(null);
      } finally {
        if (!cancel) setDemoLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [isGlobalDemo]);

  useEffect(() => {
    if (!e?.title || typeof document === "undefined") return;
    document.title = `${e.title} · Aivory EMS`;
  }, [e?.title]);

  useEffect(() => {
    if (!e?.id) return;
    bumpEventFunnel(e.id, 'landing');
  }, [e?.id]);

  const skipPageBuilder = Boolean(e?.grandfatheredLanding);

  useEffect(() => {
    if (!e?.id || isGlobalDemo || skipPageBuilder) return undefined;
    let cancel = false;
    const slug = e?.slug || slugOverride;
    if (!slug) return undefined;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/public/events/${encodeURIComponent(slug)}/landing-extras`, {
          cache: "no-store",
        });
        if (!res.ok) return;
        const data = await res.json();
        if (cancel) return;
        if (Array.isArray(data?.sessions)) setSessionsData(data.sessions);
        if (Array.isArray(data?.speakers)) setSpeakersData(data.speakers);
        if (Array.isArray(data?.sponsors)) setSponsorsData(data.sponsors);
        if (Array.isArray(data?.tickets)) setTicketsData(data.tickets);
        if (data?.branding && typeof data.branding === 'object') setWorkspaceBrand(data.branding);
        if (data?.published?.blocks?.length) setPublished(data.published);
      } catch {
        // fallback to legacy landing
      }
    })();
    return () => {
      cancel = true;
    };
  }, [e?.id, e?.slug, slugOverride, isGlobalDemo, skipPageBuilder]);

  useEffect(() => {
    if (isGlobalDemo) return undefined;
    const t = String(e?.theme || '').toLowerCase();
    if (t === 'light' || t === 'dark') setTheme(t, true);
  }, [e?.theme, setTheme, isGlobalDemo]);

  useEffect(() => {
    if (!workspaceBrand?.enabled) {
      clearTenantDocumentEffects();
      return undefined;
    }
    applyBrandDocumentEffects(workspaceBrand, {
      faviconKey: `event:${e?.id || slug || 'public'}`,
    });
    return () => {
      clearTenantDocumentEffects();
    };
  }, [workspaceBrand, e?.id, slug]);

  const registerPath = slugOverride ? '/register' : `/e/${e?.slug || slug}/register`;
  const formsgUrl = govRegistration?.backend === 'formsg' ? (govRegistration?.formUrl || '') : '';
  const registerHref = formsgUrl || registerPath;
  const registerExternal = Boolean(formsgUrl);
  const blocked = e ? getBlockedPresentation(e) : null;

  if (isGlobalDemo) {
    if (demoLoading) {
      return (
        <div className="min-h-screen grid place-items-center text-[var(--text-secondary)]" style={{ background: 'var(--bg-base)' }}>
          Loading demo event…
        </div>
      );
    }
    if (demoBundle?.event) {
      return <DemoEventLanding bundle={demoBundle} registerPath={registerPath} />;
    }
  }

  if (!e) {
    // While the bootstrap fetch is still in flight, render a neutral loading
    // state instead of flashing "Event not found" -- this is the path that
    // surfaces on the server when /api/bootstrap-data is slow.
    if (bootstrapLoading || publicLoading) {
      return (
        <div
          className="min-h-screen grid place-items-center text-[var(--text-secondary)]"
          style={{ background: 'var(--bg-base)' }}
        >
          Loading event...
        </div>
      );
    }
    return (
      <div
        className="min-h-screen grid place-items-center text-[var(--error)]"
        style={{ background: 'var(--bg-base)' }}
      >
        Event not found.
      </div>
    );
  }

  if (blocked) {
    return <PublicEventBlocked title={blocked.title} message={blocked.message} status={blocked.status} />;
  }

  const isThermeGrandfathered =
    skipPageBuilder && String(slug || e?.slug || '').trim().toLowerCase() === 'thermesggbreak';

  if (isThermeGrandfathered) {
    const thermeProps = { event: e, slug, registerPath };
    const premiumLayout =
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('layout') === 'premium';
    if (premiumLayout) {
      return <ThermeGroundbreakingLanding {...thermeProps} />;
    }
    return <ThermeGroundbreakingLandingClassic {...thermeProps} />;
  }

  // White-label custom domain mode: routes are root-relative (/, /register).
  // Canonical mode: routes include the slug (/e/<slug>, /e/<slug>/register).
  // Compute once and pass down so the deep-link buttons work in both modes
  // -- without this, clicking Register on events.example.com sends
  // the visitor to /e/thermesggbreak/register, which is not a route in the
  // white-label tree and falls through to the catch-all redirect to `/`.
  if (!skipPageBuilder && published?.blocks?.length) {
    return (
      <PublishedLanding
        event={e}
        slug={slug}
        published={published}
        sponsorsData={sponsorsData}
        speakersData={speakersData}
        sessionsData={sessionsData}
        ticketsData={ticketsData}
        brand={workspaceBrand}
        registerPath={registerHref}
        registerExternal={registerExternal}
        recommendations={recommendations}
      />
    );
  }

  return (
    <div style={{ background: 'var(--bg-base)' }} className="min-h-screen text-[var(--text-primary)]">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-[var(--bg-base-alpha70)] border-b border-[var(--border-default)]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <EventLogo event={e} brand={workspaceBrand} />
            <span className="text-[var(--border-default)] hidden sm:inline">/</span>
            <span className="text-sm text-[var(--text-secondary)] font-mono hidden sm:inline truncate">{e.slug}</span>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            {!skipPageBuilder ? (
              <>
                <a href="#schedule" className="btn-ghost text-sm hidden md:inline-flex">
                  Schedule
                </a>
                <a href="#speakers" className="btn-ghost text-sm hidden md:inline-flex">
                  Speakers
                </a>
                <a href="#venue" className="btn-ghost text-sm hidden md:inline-flex">
                  Venue
                </a>
              </>
            ) : null}
            <RegisterCta href={registerHref} external={registerExternal} className="btn-primary" testId="public-register-cta">
              {registerExternal ? 'Register on FormSG' : 'Register'} <ArrowRight size={14} />
            </RegisterCta>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-32 overflow-hidden">
        <div className="absolute inset-0">
          {e.cover ? (
            <img src={e.cover} alt="" className="w-full h-full object-cover opacity-40" />
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-base)] via-[var(--bg-base-alpha70)] to-transparent" />
        </div>
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative">
          <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-6 fade-up">
            <span className="w-1.5 h-1.5 bg-[var(--primary)] rounded-full pulse-dot" />
            {formatEventDateRange(e.startDate, e.endDate, { slash: true })}
          </div>
          <h1 className="font-heading text-[clamp(2.5rem,10vw,80px)] md:text-[150px] leading-[0.9] tracking-[-0.04em] font-semibold fade-up">
            {e.title}
            {e.startDate?.slice(0, 4) ? (
              <em className="not-italic block" style={{ color: e.accent }}>
                / {e.startDate.slice(0, 4)}
              </em>
            ) : null}
          </h1>
          {/* <p className="mt-6 md:mt-8 text-base md:text-xl text-[var(--text-secondary)] max-w-xl leading-relaxed fade-up">
            {e.subtitle}. Three days of operators, builders, and the practices that quietly compound.
          </p> */}
          <div className="mt-10 flex flex-wrap gap-3 fade-up">
            <RegisterCta href={registerHref} external={registerExternal} className="btn-primary" testId="hero-register">
              Save your seat <ArrowRight size={14} />
            </RegisterCta>
            {!skipPageBuilder ? (
              <a href="#schedule" className="btn-secondary">
                View schedule
              </a>
            ) : null}
          </div>

          <div className="py-6 px-4 mt-16 md:px-8 max-w-[1400px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
            {[
              [Calendar, 'Date', formatDateRange(e.startDate)],
              [MapPin, 'Venue', e.venue],
              [Clock, 'Timezone', (e.timezone || 'UTC').split('/')[1]?.replace('_', ' ') || e.timezone || 'UTC'],
              [Sparkles, 'Format', 'In-person'],
            ].map(([I, k, v]) => (
              <div key={k} className="bg-[var(--bg-surface)] p-5 flex flex-col gap-2">
                <I size={14} className="text-[var(--primary)]" />
                <div className="text-overline">{k}</div>
                <div
                  className={`font-mono text-sm  mt-1${k === 'Venue' ? ' max-w-[64%] md:max-w-none whitespace-pre-line' : ''} leading-relaxed break-words `}
                >
                  {k === 'Venue' && slug === 'thermesggbreak' ? `IMBA Theatre\n18 Marina Gardens Drive` : v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {!skipPageBuilder ? (
        <>
          {/* Sponsors marquee */}
          <section className="border-y border-[var(--border-default)] py-6 overflow-hidden">
            <div className="text-overline text-center mb-3">Backed by operators across the network</div>
            <div className="relative">
              <div className="flex gap-14 ticker whitespace-nowrap">
                {[
                  ...(sponsorsData.length ? sponsorsData : ['AIVORY', 'PARTNER']),
                  ...(sponsorsData.length ? sponsorsData : ['AIVORY', 'PARTNER']),
                ].map((s, i) => (
                  <span key={i} className="font-heading text-2xl tracking-[0.3em] text-[var(--border-hover)]">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </section>

          {/* Pillars */}
          <section className="py-12 md:py-24">
            <div className="max-w-[1400px] mx-auto px-4 md:px-8 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
              <div className="md:col-span-4">
                <div className="text-overline mb-4">What you'll get</div>
                <h2 className="font-heading text-2xl md:text-4xl tracking-tight font-medium leading-tight">
                  Three days. Three pillars.
                </h2>
              </div>
              <div className="md:col-span-8 grid md:grid-cols-3 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
                {[
                  ['Distribution', 'Founder-led GTM, partner systems, and the new playbooks for retention.'],
                  ['Operations', 'The CFO stack, FinOps, internal tooling — what scaling actually looks like.'],
                  ['Practice', 'Hands-on workshops with practitioners, capped to 80 seats.'],
                ].map(([t, b]) => (
                  <div key={t} className="bg-[var(--bg-surface)] p-7">
                    <div className="font-heading text-xl">{t}</div>
                    <p className="text-sm text-[var(--text-secondary)] mt-3 leading-relaxed">{b}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Schedule */}
          <section id="schedule" className="py-24 border-y border-[var(--border-default)]">
            <div className="max-w-[1400px] mx-auto px-4 md:px-8">
              <div className="text-overline mb-4">Schedule</div>
              <h2 className="font-heading text-2xl md:text-4xl tracking-tight font-medium leading-tight mb-12">Sessions</h2>
              <div className="divide-y divide-[var(--border-default)] border-y border-[var(--border-default)]">
                {(sessionsData.length ? sessionsData : []).map((s) => (
                  <div
                    key={s.id}
                    className="grid grid-cols-12 gap-4 py-6 items-center group hover:bg-[var(--bg-surface)] -mx-2 px-2 transition"
                  >
                    <div className="col-span-3 md:col-span-2 font-mono text-sm text-[var(--text-secondary)]">
                      {s.time || 'TBD'}
                    </div>
                    <div className="col-span-9 md:col-span-7">
                      <div className="font-heading text-lg leading-tight">{s.title}</div>
                      <div className="text-xs text-[var(--text-tertiary)] font-mono mt-1">{s.room}</div>
                    </div>
                    <div className="col-span-12 md:col-span-3 flex md:justify-end">
                      <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-[var(--border-default)] text-[var(--text-tertiary)]">
                        {Number(s.capacity || 0) > 0
                          ? Number(s.registered || 0) >= Number(s.capacity || 0)
                            ? 'WAITLIST'
                            : `${Math.max(0, Number(s.capacity || 0) - Number(s.registered || 0))} SEATS LEFT`
                          : 'OPEN'}
                      </span>
                    </div>
                  </div>
                ))}
                {!sessionsData.length ? (
                  <div className="py-6 text-sm text-[var(--text-tertiary)]">Schedule will be announced soon.</div>
                ) : null}
              </div>
            </div>
          </section>

          {/* Speakers */}
          <section id="speakers" className="py-24">
            <div className="max-w-[1400px] mx-auto px-4 md:px-8">
              <div className="text-overline mb-4">Speakers</div>
              <h2 className="font-heading text-2xl md:text-4xl tracking-tight font-medium leading-tight mb-12">Speakers</h2>
              <SpeakersGrid
                speakers={speakersData}
                accent="var(--primary)"
                emptyMessage="Speaker lineup will be announced soon."
              />
            </div>
          </section>

          {/* CTA */}
          <section id="venue" className="py-24 border-t border-[var(--border-default)]">
            <div className="max-w-[1400px] mx-auto px-4 md:px-8">
              <div className="text-overline mb-4">Venue · {e.venue}</div>
              <h2 className="font-heading text-[clamp(2rem,10vw,80px)] leading-[0.95] tracking-tight font-semibold">
                Be in the room.
              </h2>
              <RegisterCta href={registerHref} external={registerExternal} className="btn-primary mt-8" testId="footer-register">
                Register now <ArrowRight size={14} />
              </RegisterCta>
            </div>
          </section>
        </>
      ) : (
        <section className="py-16 md:py-24 border-t border-[var(--border-default)]">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8 text-center md:text-left">
            <h2 className="font-heading text-[clamp(2rem,8vw,64px)] leading-[0.95] tracking-tight font-semibold">
              Be in the room.
            </h2>
            <RegisterCta href={registerHref} external={registerExternal} className="btn-primary mt-8 inline-flex" testId="footer-register">
              Register now <ArrowRight size={14} />
            </RegisterCta>
          </div>
        </section>
      )}

      <footer className="border-t border-[var(--border-default)] py-10">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex flex-wrap justify-between items-center gap-4">
          <EventLogo event={e} brand={workspaceBrand} />
          <div className="text-[11px] font-mono text-[var(--text-tertiary)]">POWERED BY AIVORYEMS</div>
        </div>
      </footer>
    </div>
  );
}

function PublishedLanding({
  event,
  published,
  sponsorsData,
  speakersData = [],
  sessionsData = [],
  ticketsData = [],
  brand = null,
  registerPath,
  registerExternal = false,
  slug,
  recommendations = [],
}) {
  const themeName = published.themeName || 'Aivory Default';
  const accentFallback = String(event?.accent || brand?.colors?.primary || '').trim() || 'var(--primary)';
  const theme = {
    'Aivory Default': { accent: accentFallback, bg: '#09090B', surface: '#121214' },
    Terracotta: { accent: '#E25D3A', bg: '#0E0A08', surface: '#171210' },
    'Ice Blue': { accent: '#60A5FA', bg: '#08090E', surface: '#11131C' },
    'Editorial Cream': { accent: '#FAFAFA', bg: '#1A1815', surface: '#22201C' },
  }[themeName] || { accent: accentFallback, bg: '#09090B', surface: '#121214' };

  const previewFont = String(published.previewFont || '').trim();
  const previewSizePx = Number(published.previewSizePx) || 16;
  const fontLabels = useMemo(() => {
    const labels = [];
    GOOGLE_FONTS.forEach((row) => {
      if (previewFont.includes(row.label)) labels.push(row.label);
    });
    return labels;
  }, [previewFont]);

  useEffect(() => {
    const href = googleFontsHref(fontLabels);
    if (!href) return undefined;
    const linkId = 'published-landing-google-fonts';
    let link = document.getElementById(linkId);
    if (!link) {
      link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    link.href = href;
    return () => {
      const el = document.getElementById(linkId);
      if (el) el.remove();
    };
  }, [fontLabels]);

  const blocks = (published.blocks || []).filter((b) => b.visible !== false);
  const pageStyle = {
    background: 'var(--bg-base)',
    fontFamily: previewFont || undefined,
    fontSize: previewSizePx ? `${previewSizePx}px` : undefined,
  };
  // Background follows the document theme (light/dark from event.theme or
  // workspace fallback) so the page builder's themeName only contributes
  // accent/surface palette, not the dark-vs-light mode. Without this the
  // hardcoded dark hex from theme.bg would force a dark background even
  // when the event is configured for light mode.
  return (
    <div style={pageStyle} className="min-h-screen text-[var(--text-primary)]">
      <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-[var(--bg-base-alpha70)] border-b border-[var(--border-default)]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <EventLogo event={event} brand={brand} />
            <span className="text-[var(--border-default)]">/</span>
            <span className="text-sm text-[var(--text-secondary)] font-mono">{event.slug}</span>
          </div>
          <div className="flex items-center gap-2">
            <RegisterCta href={registerPath} external={registerExternal} className="btn-primary">
              {registerExternal ? 'Register on FormSG' : 'Register'} <ArrowRight size={14} />
            </RegisterCta>
          </div>
        </div>
      </header>
      <main className="pt-20">
        {blocks.map((b) => {
          const p = b.props || {};
          if (b.type === 'hero') {
            const heroSrc = resolveMediaUrl(p.imageUrl || event.cover);
            return (
              <section key={b.id} className="relative pt-24 pb-24 overflow-hidden">
                <div className="absolute inset-0">
                  {heroSrc ? (
                  <img src={heroSrc} alt="" className="w-full h-full object-cover opacity-35" />
                  ) : (
                  <div className="w-full h-full opacity-30" style={{ background: `linear-gradient(135deg, ${theme.accent}44, var(--bg-base))` }} />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-base)] via-[var(--bg-base-alpha70)] to-transparent" />
                </div>
                <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative">
                  <div className="text-overline mb-4">{p.eyebrow || ''}</div>
                  <h1 className="font-heading text-[72px] md:text-[120px] leading-[0.9] tracking-[-0.04em] font-semibold">
                    {p.title || event.title}
                    {p.titleAccent ? (
                      <em className="not-italic block" style={{ color: theme.accent }}>
                        {p.titleAccent}
                      </em>
                    ) : null}
                  </h1>
                  {p.body || event.subtitle ? (
                    <p className="mt-6 text-xl text-[var(--text-secondary)] max-w-xl">{p.body || event.subtitle}</p>
                  ) : null}
                  <RegisterCta href={registerPath} external={registerExternal} className="btn-primary mt-8 inline-flex">
                    {p.cta || (registerExternal ? 'Register on FormSG' : 'Register')} <ArrowRight size={14} />
                  </RegisterCta>
                </div>
              </section>
            );
          }
          if (b.type === 'text')
            return (
              <section key={b.id} className="py-12 md:py-20 px-4 md:px-8 max-w-[1400px] mx-auto">
                <h2 className="font-heading text-3xl md:text-5xl">{p.title || 'Title'}</h2>
                <p className="mt-4 text-[var(--text-secondary)] max-w-2xl">{p.body || ''}</p>
              </section>
            );
          if (b.type === 'image')
            return (
              <section key={b.id} className="py-8 md:py-12 px-4 md:px-8 max-w-[1400px] mx-auto">
                <img
                  src={resolveMediaUrl(p.src || event.cover)}
                  alt=""
                  className="w-full rounded border border-[var(--border-default)]"
                />
                {p.caption ? <p className="text-xs text-[var(--text-tertiary)] mt-2">{p.caption}</p> : null}
              </section>
            );
          if (b.type === 'gallery')
            return (
              <section
                key={b.id}
                className="py-8 md:py-12 px-4 md:px-8 max-w-[1400px] mx-auto grid grid-cols-2 md:grid-cols-3 gap-2"
              >
                {(p.images || [event.cover]).slice(0, 6).map((u, i) => (
                  <img
                    key={i}
                    src={u}
                    alt=""
                    className="w-full aspect-[4/3] object-cover border border-[var(--border-default)]"
                  />
                ))}
              </section>
            );
          if (b.type === 'accordion' || b.type === 'faq')
            return (
              <section key={b.id} className="py-10 md:py-16 px-4 md:px-8 max-w-[1000px] mx-auto">
                <h3 className="font-heading text-2xl md:text-4xl mb-6">{p.title || 'FAQ'}</h3>
                <div className="divide-y border-y border-[var(--border-default)]">
                  {(p.items || []).map((it, i) => (
                    <div key={i} className="py-4">
                      <div className="font-medium">{it.q || it.title}</div>
                      <p className="text-sm text-[var(--text-secondary)] mt-2">{it.a || it.body}</p>
                    </div>
                  ))}
                </div>
              </section>
            );
          if (b.type === 'logoStrip' || b.type === 'sponsors')
            return (
              <section key={b.id} className="py-10 border-y border-[var(--border-default)] overflow-hidden">
                <div className="max-w-[1400px] mx-auto px-4 md:px-8 text-overline mb-3">
                  {p.title || p.caption || 'Sponsors'}
                </div>
                <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex gap-10 whitespace-nowrap overflow-x-auto">
                  {(p.logos || sponsorsData || ['AIVORY']).map((s, i) => (
                    <span key={i} className="font-heading tracking-[0.2em] text-[var(--text-tertiary)]">
                      {s}
                    </span>
                  ))}
                </div>
              </section>
            );
          if (b.type === 'quote')
            return (
              <section key={b.id} className="py-12 md:py-20 px-4 md:px-8 max-w-[1000px] mx-auto">
                <Quote size={30} style={{ color: theme.accent }} />
                <p className="font-heading text-xl md:text-3xl mt-4">\"{p.quote || ''}\"</p>
                <p className="text-xs text-[var(--text-secondary)] mt-3">— {p.author || ''}</p>
              </section>
            );
          if (b.type === 'cta' || b.type === 'button')
            return (
              <section key={b.id} className="py-12 md:py-20 px-4 md:px-8 max-w-[1200px] mx-auto text-center">
                <h2 className="font-heading text-3xl md:text-6xl">{p.title || ''}</h2>
                <RegisterCta href={registerPath} external={registerExternal} className="btn-primary mt-8 inline-flex">
                  {p.cta || p.label || (registerExternal ? 'Register on FormSG' : 'Register')} <ArrowRight size={14} />
                </RegisterCta>
              </section>
            );
          if (b.type === 'metrics') {
            const customItems = Array.isArray(p.items) ? p.items.filter((it) => it?.label || it?.value) : [];
            const metricRows = customItems.length
              ? customItems.map((it, idx) => {
                  const icons = [Calendar, Clock, MapPin, Sparkles];
                  return [icons[idx % icons.length], it.label || 'Detail', it.value || ''];
                })
              : [
                  [Calendar, 'Date', formatDateRange(event.startDate)],
                  [MapPin, 'Venue', event.venue],
                  [Clock, 'Timezone', event.timezone],
                  [Sparkles, 'Format', 'In-person'],
                ];
            return (
              <section
                key={b.id}
                className="py-6 px-4 md:px-8 max-w-[1400px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border-default)] border border-[var(--border-default)]"
              >
                {metricRows.map(([I, k, v]) => (
                  <div key={`${k}-${v}`} className="bg-[var(--bg-surface)] p-5 flex flex-col gap-2">
                    <I size={14} className="text-[var(--primary)]" />
                    <div className="text-overline">{k}</div>
                    <div
                      className={`font-mono text-sm mt-1${String(k).toLowerCase() === 'venue' ? ' max-w-[64%] md:max-w-none whitespace-pre-line' : ''} leading-relaxed break-words`}
                    >
                      {String(k).toLowerCase() === 'venue' && slug === 'thermesggbreak'
                        ? 'IMBA Theatre\n18 Marina Gardens Drive'
                        : v}
                    </div>
                  </div>
                ))}
              </section>
            );
          }
          if (b.type === 'schedule') {
            return (
              <section key={b.id} id="schedule" className="py-12 md:py-20 px-4 md:px-8 max-w-[1400px] mx-auto border-y border-[var(--border-default)]">
                <div className="text-overline mb-3">{p.eyebrow || 'Schedule'}</div>
                <h2 className="font-heading text-3xl md:text-4xl tracking-tight font-medium mb-8">{p.title || 'Programme'}</h2>
                <div className="divide-y divide-[var(--border-default)] border-y border-[var(--border-default)]">
                  {(sessionsData.length ? sessionsData : []).map((s) => (
                    <div
                      key={s.id}
                      className="grid grid-cols-12 gap-4 py-6 items-center group hover:bg-[var(--bg-surface)] -mx-2 px-2 transition"
                    >
                      <div className="col-span-3 md:col-span-2 font-mono text-sm text-[var(--text-secondary)]">
                        {s.time || 'TBD'}
                      </div>
                      <div className="col-span-9 md:col-span-7">
                        <div className="font-heading text-lg leading-tight">{s.title}</div>
                        <div className="text-xs text-[var(--text-tertiary)] font-mono mt-1">{s.room}</div>
                        {s.description ? (
                          <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-2xl">{s.description}</p>
                        ) : null}
                      </div>
                      <div className="col-span-12 md:col-span-3 flex md:justify-end">
                        <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-[var(--border-default)] text-[var(--text-tertiary)]">
                          {Number(s.capacity || 0) > 0
                            ? Number(s.registered || 0) >= Number(s.capacity || 0)
                              ? 'WAITLIST'
                              : `${Math.max(0, Number(s.capacity || 0) - Number(s.registered || 0))} SEATS LEFT`
                            : 'OPEN'}
                        </span>
                      </div>
                    </div>
                  ))}
                  {!sessionsData.length ? (
                    <div className="py-6 text-sm text-[var(--text-tertiary)]">Schedule will be announced soon.</div>
                  ) : null}
                </div>
              </section>
            );
          }
          if (b.type === 'tickets') {
            const tiers = ticketsData.length
              ? ticketsData
              : Array.isArray(p.tiers)
                ? p.tiers
                : [];
            return (
              <section key={b.id} id="tickets" className="py-12 md:py-20 px-4 md:px-8 max-w-[1400px] mx-auto">
                <div className="text-overline mb-3">{p.eyebrow || 'Registration'}</div>
                <h2 className="font-heading text-3xl md:text-4xl tracking-tight font-medium">{p.title || 'Choose your pass'}</h2>
                {p.subtitle ? (
                  <p className="mt-3 text-[var(--text-secondary)] max-w-2xl">{p.subtitle}</p>
                ) : null}
                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
                  {(tiers.length ? tiers : [{ id: 'open', name: 'General admission', price: 0, status: 'Open' }]).map((t) => {
                    const quota = Number(t?.quota || 0);
                    const sold = Number(t?.sold || 0);
                    const left = Math.max(0, quota - sold);
                    const soldOut = quota > 0 && sold >= quota;
                    const price = Number(t?.price || 0);
                    return (
                      <div key={t?.id || t?.name} className="bg-[var(--bg-surface)] p-6 flex flex-col gap-3">
                        <div className="font-heading text-xl">{t?.name || 'Untitled tier'}</div>
                        <div className="font-mono text-3xl" style={{ color: theme.accent }}>
                          {price <= 0 ? 'Free' : `$${price.toLocaleString()}`}
                        </div>
                        <div className="text-[10px] uppercase tracking-widest font-mono text-[var(--text-tertiary)]">
                          {soldOut ? 'Sold out' : left > 0 ? `${left} left` : t?.status || 'Open'}
                        </div>
                        {!soldOut ? (
                          <RegisterCta href={registerPath} external={registerExternal} className="btn-primary mt-2 inline-flex self-start">
                            Register <ArrowRight size={14} />
                          </RegisterCta>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          }
          if (b.type === 'speakers') {
            return (
              <section key={b.id} id="speakers" className="py-12 md:py-20 px-4 md:px-8 max-w-[1400px] mx-auto">
                <div className="text-overline mb-3">{p.eyebrow || 'Speakers'}</div>
                <h2 className="font-heading text-3xl md:text-4xl tracking-tight font-medium mb-8">{p.title || 'Speakers'}</h2>
                <SpeakersGrid
                  speakers={speakersData}
                  accent={theme.accent}
                  emptyMessage="Speaker lineup will be announced soon."
                />
              </section>
            );
          }
          return null;
        })}
        {recommendations.length > 0 ? (
          <section className="py-12 md:py-16 px-4 md:px-8 max-w-[1000px] mx-auto border-t border-[var(--border-default)]" data-testid="event-recommendations">
            <div className="flex items-center gap-2 text-[var(--primary)] mb-3">
              <Sparkles size={16} />
              <span className="text-overline">Recommended for you</span>
            </div>
            <h2 className="font-heading text-2xl md:text-3xl tracking-tight font-medium mb-6">Sessions & highlights</h2>
            <ul className="space-y-4">
              {recommendations.map((hit, i) => (
                <li key={hit.id || i} className="surface p-4 border border-[var(--border-default)]">
                  <div className="font-medium text-[var(--text-primary)]">{hit.title || hit.snippet || 'Session'}</div>
                  {hit.snippet && hit.title ? (
                    <p className="text-sm text-[var(--text-secondary)] mt-2 line-clamp-2">{hit.snippet}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </main>
      <footer className="border-t border-[var(--border-default)] py-10 mt-10">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex justify-between items-center">
          <EventLogo event={event} brand={brand} />
          <div className="text-[11px] font-mono text-[var(--text-tertiary)]">PUBLISHED PAGE</div>
        </div>
      </footer>
    </div>
  );
}
